/**
 * KeyValueTable — a flow's named constants as rows, not as YAML in a textarea.
 *
 * `defaults` and `pack_assumptions` were free-text fields: one `key: value` per
 * line, parsed on save. That asks the author to be a YAML parser — a stray
 * colon, a missing space or a typo'd key was accepted silently and only showed
 * up as a flow that ranked differently. These are seven deratings and
 * efficiencies, not prose.
 *
 * PER-KEY LOCKING. After a MATLAB import most of these belong to the script, but
 * not all of them: `ambient_c` and `form_factor_preference` are ours and no
 * script mentions them. So the lock is per row, driven by `lockedKeys` — which
 * the backend states in `matlab_source.owns`, rather than the frontend keeping a
 * second copy of the import's vocabulary.
 */
import { RowControls } from './RowControls.jsx'

let _seq = 0

/** {a: 1, b: 'x'} → [{id, key, value}] (stable ids so a row keeps focus). */
export function mapToRows(obj) {
  return Object.entries(obj || {}).map(([key, value]) => ({
    id: `kv${++_seq}`,
    key,
    value: String(value),
  }))
}

/** Rows → {a: 1, b: 'x'} — numbers stay numbers, everything else is a string. */
export function rowsToMap(rows) {
  const out = {}
  for (const row of rows || []) {
    const key = (row.key || '').trim()
    if (!key) continue
    const raw = String(row.value ?? '').trim()
    const num = Number(raw)
    out[key] = raw !== '' && Number.isFinite(num) ? num : raw
  }
  return out
}

export function KeyValueTable({
  rows,
  onChange,
  lockedKeys = [],
  addLabel = '+ Add entry',
  keyPlaceholder = 'key',
  valuePlaceholder = 'value',
  emptyHint = 'Nothing set — the code defaults apply.',
}) {
  const locked = new Set(lockedKeys)
  const update = (i, patch) =>
    onChange(rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)))

  return (
    <div className="admin-flow-kv">
      {rows.length === 0 && (
        <p className="admin-aim-empty admin-flow-empty">{emptyHint}</p>
      )}
      <ul className="admin-flow-list" role="list">
        {rows.map((r, i) => {
          const isLocked = locked.has(r.key)
          return (
            <li key={r.id} className="admin-flow-kv-row">
              <input
                className="admin-aim-input admin-flow-kv-key"
                type="text"
                value={r.key}
                disabled={isLocked}
                placeholder={keyPlaceholder}
                aria-label={`Entry ${i + 1} key`}
                onInput={(e) => update(i, { key: e.currentTarget.value })}
              />
              <input
                className="admin-aim-input admin-flow-kv-value"
                type="text"
                value={r.value}
                disabled={isLocked}
                placeholder={valuePlaceholder}
                aria-label={`Entry ${i + 1} value`}
                onInput={(e) => update(i, { value: e.currentTarget.value })}
              />
              {isLocked ? (
                // Says WHY it cannot be edited. A greyed-out field with no
                // explanation is the thing people file a bug about.
                <span className="admin-flow-kv-lock" title="Imported from the MATLAB script">
                  from script
                </span>
              ) : (
                <RowControls
                  index={i}
                  count={rows.length}
                  label={`entry ${i + 1}`}
                  onRemove={() => onChange(rows.filter((_, idx) => idx !== i))}
                />
              )}
            </li>
          )
        })}
      </ul>
      <button
        type="button"
        className="admin-ai-btn admin-ai-btn--ghost admin-flow-add"
        onClick={() => onChange([...rows, { id: `kv${++_seq}`, key: '', value: '' }])}
      >
        {addLabel}
      </button>
    </div>
  )
}
